/**
 * 問い合わせバリデーションスキーマ
 */

import { z } from "zod";

// ============================================
// 基本スキーマ
// ============================================

/** 問い合わせカテゴリスキーマ */
export const ContactCategorySchema = z.enum(["bug", "feature", "feedback", "other"]);

/** メールアドレススキーマ */
export const ContactEmailSchema = z
  .string()
  .min(1, "メールアドレスを入力してください")
  .email("有効なメールアドレスを入力してください")
  .max(255, "メールアドレスは255文字以内で入力してください");

/** 名前スキーマ（任意） */
export const ContactNameSchema = z
  .string()
  .max(50, "名前は50文字以内で入力してください")
  .optional()
  .transform((v) => v?.trim() || undefined);

/** 件名スキーマ */
export const ContactSubjectSchema = z
  .string()
  .min(1, "件名を入力してください")
  .max(100, "件名は100文字以内で入力してください");

/** 本文スキーマ */
export const ContactMessageSchema = z
  .string()
  .min(10, "お問い合わせ内容は10文字以上で入力してください")
  .max(5000, "お問い合わせ内容は5000文字以内で入力してください");

// ============================================
// リクエストスキーマ
// ============================================

/** 問い合わせリクエストスキーマ */
export const ContactRequestSchema = z.object({
  category: ContactCategorySchema,
  email: ContactEmailSchema,
  name: ContactNameSchema,
  subject: ContactSubjectSchema,
  message: ContactMessageSchema,
});

// ============================================
// レスポンススキーマ
// ============================================

/** 問い合わせ成功レスポンススキーマ */
export const ContactResponseSchema = z.object({
  success: z.literal(true),
  message: z.string(),
  contactId: z.string(),
});

// ============================================
// 保存レコードスキーマ
// ============================================

/** 問い合わせステータススキーマ */
export const ContactStatusSchema = z.enum(["pending", "reviewed", "resolved"]);

// ============================================
// 対応履歴スキーマ
// ============================================

/** 対応アクション種別スキーマ */
export const ContactActionTypeSchema = z.enum(["status_changed", "note_added", "reply_sent"]);

/** ステータス変更詳細スキーマ */
export const StatusChangedDetailsSchema = z.object({
  type: z.literal("status_changed"),
  from: ContactStatusSchema,
  to: ContactStatusSchema,
  note: z.string().optional(),
});

/** メモ追加詳細スキーマ */
export const NoteAddedDetailsSchema = z.object({
  type: z.literal("note_added"),
  content: z.string(),
});

/** 返信送信詳細スキーマ */
export const ReplySentDetailsSchema = z.object({
  type: z.literal("reply_sent"),
  subject: z.string(),
  message: z.string(),
});

/** アクション詳細スキーマ（union） */
export const ContactActionDetailsSchema = z.discriminatedUnion("type", [
  StatusChangedDetailsSchema,
  NoteAddedDetailsSchema,
  ReplySentDetailsSchema,
]);

/** 対応履歴エントリスキーマ */
export const ContactActionEntrySchema = z.object({
  id: z.string().uuid(),
  type: ContactActionTypeSchema,
  performedBy: z.string().email(),
  performedAt: z.string(),
  details: ContactActionDetailsSchema,
});

// ============================================
// 保存レコードスキーマ
// ============================================

/** 問い合わせ保存レコードスキーマ */
export const ContactRecordSchema = z.object({
  id: z.string().uuid(),
  category: ContactCategorySchema,
  email: ContactEmailSchema,
  name: z.string().optional(),
  subject: z.string(),
  message: z.string(),
  userId: z.string().optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.string(),
  status: ContactStatusSchema,
  actions: z.array(ContactActionEntrySchema),
  lastActionAt: z.string().nullable(),
});

// ============================================
// 管理者API用スキーマ
// ============================================

/** 問い合わせ一覧取得クエリスキーマ */
export const ContactListQuerySchema = z.object({
  status: ContactStatusSchema.optional(),
  category: ContactCategorySchema.optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sortBy: z.enum(["createdAt", "lastActionAt"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});

/** ステータス更新リクエストスキーマ */
export const UpdateContactStatusSchema = z.object({
  status: ContactStatusSchema,
  note: z.string().max(500).optional(),
});

/** メモ追加リクエストスキーマ */
export const AddContactNoteSchema = z.object({
  content: z
    .string()
    .min(1, "メモを入力してください")
    .max(2000, "メモは2000文字以内で入力してください"),
});

/** 返信送信リクエストスキーマ */
export const SendContactReplySchema = z.object({
  subject: z
    .string()
    .min(1, "件名を入力してください")
    .max(200, "件名は200文字以内で入力してください"),
  message: z
    .string()
    .min(1, "本文を入力してください")
    .max(10000, "本文は10000文字以内で入力してください"),
});

// ============================================
// 型推論
// ============================================

/** 問い合わせリクエスト型（スキーマから推論） */
export type ContactRequestFromSchema = z.infer<typeof ContactRequestSchema>;

/** 問い合わせレコード型（スキーマから推論） */
export type ContactRecordFromSchema = z.infer<typeof ContactRecordSchema>;

/** 問い合わせ一覧クエリ型（スキーマから推論） */
export type ContactListQuery = z.infer<typeof ContactListQuerySchema>;

/** ステータス更新リクエスト型 */
export type UpdateContactStatusRequest = z.infer<typeof UpdateContactStatusSchema>;

/** メモ追加リクエスト型 */
export type AddContactNoteRequest = z.infer<typeof AddContactNoteSchema>;

/** 返信送信リクエスト型 */
export type SendContactReplyRequest = z.infer<typeof SendContactReplySchema>;
